/**
 * AssetDetailModal — Feb 2026.
 *
 * Photo viewer + a single **Download Original** button.
 *
 * Direct-download via fetch → blob → hidden <a download="…">.click().
 * The Bunny CDN sends ``access-control-allow-origin: *`` so cross-
 * origin fetch works from the browser without a proxy. Filename is
 * derived from the project name (sanitized) so a client receives
 * ``ProjectTitle_Original.jpg`` rather than the internal UUID.
 */
import { useEffect, useState } from "react";
import { Download, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

function _mimeToExt(mime) {
  if (!mime) return "jpg";
  const m = mime.toLowerCase();
  if (m.includes("jpeg") || m.includes("jpg")) return "jpg";
  if (m.includes("png")) return "png";
  if (m.includes("webp")) return "webp";
  if (m.includes("gif")) return "gif";
  return "jpg";
}

function _sanitize(name) {
  return (name || "photo")
    .trim()
    .replace(/[^\w-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80) || "photo";
}

export default function AssetDetailModal({ photo, projectName, canDownload = true, onClose }) {
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleDownload = async () => {
    if (saving) return;
    const filename = `${_sanitize(projectName || photo.title)}_Original.${_mimeToExt(photo.mime)}`;
    setSaving(true);
    let objectUrl = null;
    try {
      const res = await fetch(photo.cdn_url, { mode: "cors" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      toast.error(`Download failed: ${err?.message || err}`);
    } finally {
      if (objectUrl) {
        // Delay revoke slightly so Safari has time to hand the blob
        // off to the download manager.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
      }
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="asset-detail-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-zinc-950 border border-zinc-800 rounded-lg overflow-hidden max-w-5xl w-full max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-900 shrink-0">
          <div className="min-w-0 flex-1">
            <h3
              className="font-heading text-lg font-light text-zinc-100 truncate"
              title={photo.title}
            >
              {photo.title}
            </h3>
            <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-0.5">
              {photo.width_px && photo.height_px
                ? `${photo.width_px} × ${photo.height_px}`
                : "photo"}
              {photo.uploader_name ? ` · ${photo.uploader_name}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="asset-detail-close-btn"
            className="text-zinc-500 hover:text-zinc-200 p-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 bg-zinc-900/40 flex items-center justify-center overflow-auto">
          {photo.cdn_url ? (
            <img
              src={photo.cdn_url}
              alt={photo.title}
              className="max-w-full max-h-full object-contain"
              data-testid="asset-detail-image"
            />
          ) : (
            <Loader2 className="w-6 h-6 text-zinc-600 animate-spin" />
          )}
        </div>

        {canDownload && (
          <div className="border-t border-zinc-900 p-4 shrink-0 flex justify-center">
            <Button
              type="button"
              data-testid="asset-download-original-btn"
              onClick={handleDownload}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-400 text-black h-11 px-6 gap-2 min-w-[220px]"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Saving…
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" /> Download Original
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
